import { stateFacilityIdIssue } from './state-facility-id.ts';
import type { FacilityMeta } from './tier2submit-export.ts';
import { isValidPhone } from './phone.ts';
import { contactHasFirstAndLastName } from './contact-name.ts';
import type { SdsApplicability, TierIIDocumentKind } from '../../tri-shared/types/tierii-state-fields.ts';
import { ATTACHMENT_FIELDS, STATE_POLICIES, attachmentFieldApplies } from '../data/state-rules-2025.ts';
import {
  resolveFacilityStateFields,
  stateFieldIssues,
  stateFieldsForForm,
  STATE_FIELDS_YEAR,
  type StateDocumentStatus,
} from './state-fields.ts';

export const DOCUMENT_LABELS: Record<TierIIDocumentKind, string> = {
  sds: 'Safety Data Sheets',
  'sds-explanation': 'SDS explanation',
  'site-plan': 'Site map / site plan',
  'floor-plan': 'Floor plan',
  'rcra-plan': 'RCRA contingency plan',
  certification: 'Signed Tier II certification',
  'confidential-location': 'Confidential location form',
  'dike-description': 'Spill containment and storage safeguards',
  'coordinate-abbreviations': 'Site map abbreviation key',
  'emergency-response-plan': 'Emergency response plan',
  'training-plan': 'Employee training plan',
  'ehs-response-plan': 'EHS emergency planning documents',
};
/** Translate the existing admin vocabulary once, keeping profile values intact. */
const attachmentKinds: Record<string, TierIIDocumentKind> = {
  site_plan: 'site-plan',
  site_map: 'site-plan',
  facility_map: 'site-plan',
  sds: 'sds',
  sds_all_reported_chemicals: 'sds',
  sds_each_reported_chemical: 'sds',
  sds_new_or_updated: 'sds',
  sds_newly_reported_chemicals: 'sds',
  emergency_response_plan: 'emergency-response-plan',
  facility_emergency_response_plan: 'emergency-response-plan',
  employee_training_plan: 'training-plan',
  description_of_dikes_and_safeguards: 'dike-description',
  site_coordinate_abbreviations_list: 'coordinate-abbreviations',
  certification_letter: 'certification',
  ehs_worksheet_or_emergency_response_plan_with_site_plan: 'ehs-response-plan',
};
export type ResolvedJurisdictionRequirements = ReturnType<typeof resolveJurisdictionRequirements>;
export function hasStateSdsRequirements(requirements: ResolvedJurisdictionRequirements): boolean {
  return requirements.requiresAllSds || requirements.sdsConditions.length > 0;
}
export function reportSdsIncluded(requirements: ResolvedJurisdictionRequirements, includeOptional: boolean): boolean {
  return hasStateSdsRequirements(requirements) || includeOptional;
}
export function resolveJurisdictionRequirements(meta: FacilityMeta) {
  const year = meta.reportingYear;
  const policy = year === STATE_FIELDS_YEAR ? (STATE_POLICIES[meta.state] ?? {}) : {};
  const profile =
    meta.jurisdictionProfile?.stateCode === meta.state && meta.jurisdictionProfile.reportingYear === year
      ? meta.jurisdictionProfile
      : null;
  const fields = stateFieldsForForm(resolveFacilityStateFields(meta), meta.state, 'facility');
  const attachments = new Map<TierIIDocumentKind, { kind: TierIIDocumentKind; required: boolean; sources: string[] }>();
  const issues: string[] = [];
  const questions: { key: 'newOrUpdatedFacility' | 'sitePlanRequested'; label: string }[] = [];
  const sdsConditions: SdsApplicability[] = [];
  const add = (kind: TierIIDocumentKind, required: boolean, source: string) => {
    const previous = attachments.get(kind);
    attachments.set(kind, {
      kind,
      required: required || previous?.required === true,
      sources: [...new Set([...(previous?.sources ?? []), source])],
    });
  };
  if (year === STATE_FIELDS_YEAR) {
    if (meta.state === 'AK' || meta.state === 'RI') add('site-plan', true, 'Tier2 Submit ' + year);
    if (meta.state === 'RI') {
      add('coordinate-abbreviations', true, 'Tier2 Submit ' + year);
      add('dike-description', true, 'Tier2 Submit ' + year);
    }
  }
  for (const field of fields) {
    const kind = ATTACHMENT_FIELDS[meta.state]?.[field.field];
    if (kind && attachmentFieldApplies(meta.state, field.field, fields))
      add(kind, field.required, `Tier2 Submit ${year}: ${field.label}`);
  }
  let sitePlanApplies: boolean | null = null;
  if (
    profile &&
    (profile.sitePlanRequired === true || profile.requiredAttachments.some((id) => attachmentKinds[id] === 'site-plan'))
  ) {
    const when = profile.sitePlanRequiredWhen ?? 'always';
    if (when === 'always') sitePlanApplies = true;
    else if (when === 'epcra_302_facilities_only') {
      sitePlanApplies = meta.subjectToSection302 ?? null;
      if (sitePlanApplies == null)
        issues.push(
          'Answer whether the facility is subject to EPCRA Section 302 to determine its site-plan requirement.',
        );
    } else if (when === 'new_or_updated_facility_only' || when === 'on_request') {
      const key = when === 'on_request' ? 'sitePlanRequested' : 'newOrUpdatedFacility';
      const label =
        when === 'on_request'
          ? 'Has the jurisdiction requested a site plan for this report?'
          : 'Is this a new or updated facility filing?';
      questions.push({ key, label });
      sitePlanApplies = meta.stateReporting.applicability[key] ?? null;
      if (sitePlanApplies == null) issues.push(label);
    } else issues.push(`Unrecognized site-plan condition in the jurisdiction profile: ${when}.`);
    if (sitePlanApplies !== false) add('site-plan', sitePlanApplies === true, `Jurisdiction profile ${year}`);
  }
  for (const id of profile?.requiredAttachments ?? []) {
    if (id === 'latitude_longitude') {
      if (meta.latitude == null || meta.longitude == null)
        issues.push('Latitude and longitude are required by the jurisdiction profile.');
      continue;
    }
    const kind = attachmentKinds[id];
    if (!kind) {
      issues.push(`Unrecognized attachment requirement in the jurisdiction profile: ${id}.`);
      continue;
    }
    if (id === 'sds_new_or_updated' || id === 'sds_newly_reported_chemicals') {
      sdsConditions.push(id === 'sds_new_or_updated' ? 'newOrUpdated' : 'newlyReported');
      add(kind, false, `Jurisdiction profile ${year}: ${id}`);
    } else if (kind === 'site-plan' || kind === 'ehs-response-plan') {
      // An explicitly false condition is not an invitation to upload an optional document.
      // Existing selections remain available in the form for review/removal.
      if (sitePlanApplies !== false) add(kind, sitePlanApplies === true, `Jurisdiction profile ${year}`);
    } else add(kind, true, `Jurisdiction profile ${year}`);
  }
  if (profile?.sitePlanRequired === false && attachments.get('site-plan')?.required)
    issues.push(
      'The jurisdiction profile says no site plan, but Tier2 Submit requires one. Reconcile the jurisdiction profile.',
    );
  const requiresAllSds = attachments.get('sds')?.required === true;
  return {
    state: meta.state,
    reportingYear: year,
    profile,
    fields,
    attachments: [...attachments.values()],
    requiresAllSds,
    sdsConditions: requiresAllSds ? [] : [...new Set(sdsConditions)],
    allowSdsExplanation: policy.sdsExplanation === true,
    county: policy.county ?? null,
    equipmentContactRequired: policy.equipmentContact === true,
    minimumAttachments: policy.minimumAttachments ?? 0,
    minimumEmergencyContacts: policy.minimumEmergencyContacts ?? 0,
    fireDistrictRequired: policy.fireDistrict === true,
    triIdRequired: fields.some((f) => f.field === policy.triReportingField && f.value === true),
    questions,
    issues,
    sitePlanRequired:
      attachments.get('site-plan')?.required === true
        ? true
        : (sitePlanApplies ?? (profile?.sitePlanRequired === false ? false : null)),
  };
}
export function sdsRequiredForProduct(
  requirements: ResolvedJurisdictionRequirements,
  meta: FacilityMeta,
  productID: number,
): boolean {
  return (
    requirements.requiresAllSds ||
    requirements.sdsConditions.some(
      (key) => meta.stateReporting.productSdsApplicability[String(productID)]?.[key] === true,
    )
  );
}
export function facilityFireDistrictIssues(meta: FacilityMeta): string[] {
  return resolveJurisdictionRequirements(meta).fireDistrictRequired && !meta.fireDistrict.trim()
    ? [`${meta.state}: Fire district is required.`]
    : [];
}

export function attachmentFileCountIssue(
  requirements: ResolvedJurisdictionRequirements,
  status: StateDocumentStatus | null,
): string | null {
  if (!status || status.fileCount >= requirements.minimumAttachments) return null;
  return `At least ${requirements.minimumAttachments} separate files must be included; ${status.fileCount} currently included. A saved site map counts as one file. A file selected for multiple document categories is exported as a separately named copy for each category.`;
}

export function facilityStateIssues(meta: FacilityMeta): string[] {
  const idIssue = stateFacilityIdIssue(meta.stateReporting, meta.state);
  const idIssues = idIssue ? [idIssue] : [];
  if (meta.reportingYear !== STATE_FIELDS_YEAR)
    return [
      ...idIssues,
      `State requirements for ${meta.reportingYear} have not been loaded; the available catalog is for ${STATE_FIELDS_YEAR}.`,
    ];
  const r = resolveJurisdictionRequirements(meta);
  // Attachment fields are derived, so validate their actual document coverage once below.
  const issues = [
    ...idIssues,
    ...r.issues,
    ...stateFieldIssues(
      r.fields.filter((f) => !ATTACHMENT_FIELDS[meta.state]?.[f.field]),
      meta.state,
      'facility',
    ),
  ];
  const review = meta.stateReporting.stateCorrectionReview;
  if (review && review.reviewed === null)
    issues.push(
      `Review location details, local contacts, state answers, and documents after the state correction from ${review.fromState} to ${review.toState}.`,
    );
  if (r.county && !r.county.choices.includes(meta.county ?? ''))
    issues.push(`Choose a standard ${r.county.label.toLowerCase()}.`);
  if (
    r.equipmentContactRequired &&
    !meta.contacts?.some(
      (c) =>
        contactHasFirstAndLastName(c) &&
        c.contactTypes.includes('Emergency Equipment Contact') &&
        c.phones?.some((p) => p.type === '24-hour' && isValidPhone(p.number)),
    )
  )
    issues.push(
      'An Emergency Equipment Contact with a 24-hour phone number is required. Select the role on an existing emergency contact.',
    );
  if (r.equipmentContactRequired) {
    for (const contact of meta.contacts.filter((contact) =>
      contact.contactTypes.includes('Emergency Equipment Contact'),
    )) {
      if (!contactHasFirstAndLastName(contact))
        issues.push(
          contact.nameIsBusiness
            ? contact.name +
                ': an Emergency Equipment Contact must be a person with a first and last name, not a business.'
            : 'Emergency Equipment Contact: first and last name are required.',
        );
      if (!contact.phones.some((phone) => phone.type === '24-hour' && isValidPhone(phone.number)))
        issues.push(contact.name + ': Emergency Equipment Contact requires a valid 24-hour phone.');
    }
  }
  for (const doc of r.attachments) {
    if (!doc.required) continue;
    const covered = doc.kind === 'sds' ? meta.documentStatus.sdsComplete : meta.documentStatus.kinds.includes(doc.kind);
    if (!covered) issues.push(`A report attachment is needed: ${DOCUMENT_LABELS[doc.kind]}.`);
  }
  const fileCountIssue = attachmentFileCountIssue(r, meta.documentStatus);
  if (fileCountIssue) issues.push(fileCountIssue);
  if (
    (meta.contacts?.filter((c) => c.name.trim() && c.contactTypes.includes('Emergency Contact')).length ?? 0) <
    r.minimumEmergencyContacts
  )
    issues.push(`At least ${r.minimumEmergencyContacts} emergency contacts are required.`);
  if (r.triIdRequired && !meta.triID?.trim()) issues.push('A TRI ID is required when reporting under TRI.');
  return [...new Set(issues)].map((issue) => `${meta.state}: ${issue}`).concat(facilityFireDistrictIssues(meta));
}
